import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

const slides = [
  {
    id: 1,
    title: '변덕스러운 기후에도',
    highlight: '필드는 언제나 완벽한 그린',
    titleEnd: '',
    subtitle:
      '서리 피해 경감부터 동해 예방까지. KW 테크만의 기술력으로 사계절 내내 최상의 잔디 컨디션을 약속합니다.',
    cta: '제품 보러가기',
    image:
      'https://i.imgur.com/9CSVZW8.jpeg',
  },
  {
    id: 2,
    title: '뿌리부터 다른 잔디',
    highlight: '압도적인 잔디 밀도',
    titleEnd: '',
    subtitle:
      '단순한 영양 공급을 넘어 골프장의 품격을 결정하는 디테일한 생육 솔루션을 제공합니다.',
    cta: '솔루션 알아보기',
    image:
      'https://public.readdy.ai/ai/img_res/aa56f0ffebaf08604e27b7a52fd64034.jpg',
  },
  {
    id: 3,
    title: '17가지 커스텀 솔루션',
    highlight: '골프장의 모든 고민 해결',
    titleEnd: '',
    subtitle:
      '병해 방제부터 기능성 영양제까지. KW 테크의 전문 라인업이 대한민국 골프장의 건강한 생태계를 설계합니다.',
    cta: '품질 확인하기',
    image:
      'https://i.imgur.com/W79cLvk.jpeg',
  },
];

export default function HeroSlider() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const isSwiping = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Reset the auto‑slide timer
  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 7000);
  }, []);

  // Initialise timer on mount and clean up on unmount
  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resetTimer]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    resetTimer();
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    resetTimer();
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    resetTimer();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    isSwiping.current = true;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwiping.current) return;
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!isSwiping.current) return;
    isSwiping.current = false;
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;
    if (Math.abs(diff) > threshold) {
      diff > 0 ? nextSlide() : prevSlide();
    }
  };

  // Defensive: ensure we don't attempt to access window during SSR
  const lineHeightStyle = typeof window !== 'undefined' && window.innerWidth >= 768 ? '1.46' : undefined;

  return (
    <div
      className="relative h-[462px] md:h-[58vh] w-full overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="absolute inset-0 w-full h-full">
            <img src={slide.image} alt={slide.title} className="w-full h-full object-cover object-center" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>

          <div className="relative h-full max-w-[1400px] mx-auto px-5 flex items-center">
            <div className="w-full max-w-xl md:pl-[10%] px-4 md:px-0">
              <h1
                className="text-2xl md:text-4xl font-bold text-white mb-5 md:mb-5 leading-tight text-center md:text-left break-keep"
                style={{ lineHeight: lineHeightStyle }}
              >
                {slide.title}
                <br />
                <span className="text-white">{slide.highlight}</span>
                {slide.titleEnd}
              </h1>
              <p className="text-sm md:text-base text-white/90 mb-8 leading-relaxed text-center md:text-left break-keep">
                {slide.subtitle}
              </p>
            </div>
          </div>
        </div>
      ))}

      {/* 화살표 - PC만 */}
      <div className="hidden md:block absolute inset-y-0 left-0 right-0 max-w-[1400px] mx-auto px-5 pointer-events-none">
        <button
          onClick={prevSlide}
          className="absolute left-6 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all z-10 cursor-pointer pointer-events-auto"
        >
          <i className="ri-arrow-left-line text-lg"></i>
        </button>

        <button
          onClick={nextSlide}
          className="absolute right-6 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all z-10 cursor-pointer pointer-events-auto"
        >
          <i className="ri-arrow-right-line text-lg"></i>
        </button>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
              index === currentSlide ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/70'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
